import { ShieldCheck, UserCheck, AlertTriangle } from "lucide-react";

/**
 * Section "Akun Login Portal" — dipakai di modal Employee Add/Edit.
 * Ditarik keluar dari Employees.jsx agar tidak ter-duplikat.
 *
 * Kontrol role/template/reset password mengikuti gerbang SERVER
 * (`employees._pastikan_boleh_atur_akun`, KEPUTUSAN-TERTUNDA E29) supaya tidak
 * ada kontrol yang ujungnya 403 (aturan #3): role & template hanya bisa diubah
 * superadmin/admin; akun superadmin hanya oleh superadmin; akun sendiri tidak
 * bisa diubah role/template-nya; staf HR hanya menyentuh akun ber-role employee.
 */

/**
 * Aturan yang sama dengan gerbang server, dipakai bagian ini DAN form induknya
 * (medan Email ikut terkunci untuk akun terlindung — mengganti email karyawan
 * ikut mengganti email LOGIN akunnya).
 * "Akun sendiri" dikenali lewat `employee_id`, persis cara server menemukan akun
 * tertaut (`users.employee_id`) — bukan lewat email, yang bisa berbeda antara
 * data karyawan dan akun login.
 */
export function aturanAkunKaryawan({ form, isEdit, pelaku }) {
  const peran = pelaku?.role;
  const bolehAturRole = peran === "superadmin" || peran === "admin";
  const roleAkun = form.login_role || "employee";
  const punyaAkun = isEdit && !!form.has_login_account;
  const akunSendiri = punyaAkun && !!pelaku?.employee_id && pelaku.employee_id === form.id;
  const akunSuperadmin = isEdit && roleAkun === "superadmin";
  const kunciRole = !bolehAturRole || akunSendiri || (akunSuperadmin && peran !== "superadmin");
  const akunTerlindungi = punyaAkun && (
    (akunSuperadmin && peran !== "superadmin") || (!bolehAturRole && roleAkun !== "employee"));
  return { peran, roleAkun, akunSendiri, akunSuperadmin, kunciRole, akunTerlindungi };
}

export default function EmployeeAccountSection({
  form, upd, isEdit, permTemplates = [], pelaku = null,
  Field, Input, Select, LOGIN_ROLES,
}) {
  const { roleAkun, akunSendiri, akunSuperadmin, kunciRole, akunTerlindungi } =
    aturanAkunKaryawan({ form, isEdit, pelaku });
  const hasAccount = !!form.has_login_account;
  const showAccountSection = !isEdit ? true : (hasAccount || form.enable_login_account);
  const suggestedPwd = (form.nik || "").slice(-6);
  const wantsAccount = !!(form.create_login_account || form.enable_login_account);
  const missingEmail = wantsAccount && !(form.email || "").trim();

  return (
    <div>
      <h4 className="font-display font-semibold text-slate-900 border-b border-slate-100 pb-2 mb-4 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        Akun Login Portal
        {isEdit && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${hasAccount ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
            {hasAccount ? "Sudah punya akun" : "Belum punya akun"}
          </span>
        )}
      </h4>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 mb-4">
        <UserCheck className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
        {isEdit
          ? "Ubah role/template atau reset password akun login karyawan ini. Email & nama akun akan tersinkron otomatis."
          : "Akun login otomatis dibuat saat simpan. Email di section Informasi Pribadi wajib diisi. Password default = 6 digit terakhir NIK."}
      </div>

      {missingEmail && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 text-xs text-amber-800 mb-4 flex items-start gap-2"
          data-testid="warning-missing-email">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Email kosong — akun login tidak akan dibuat</p>
            <p className="mt-1">Isi field <b>Email</b> di section Informasi Pribadi. Tanpa email, ceklis di bawah akan diabaikan &amp; toast error muncul saat simpan.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isEdit && (
          <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
            <input type="checkbox" checked={!!form.create_login_account}
              onChange={e => upd("create_login_account", e.target.checked)}
              data-testid="input-create-account"
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <span className="text-sm font-medium text-slate-700">Buatkan akun login otomatis untuk karyawan ini</span>
          </label>
        )}
        {isEdit && !hasAccount && (
          <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
            <input type="checkbox" checked={!!form.enable_login_account}
              onChange={e => upd("enable_login_account", e.target.checked)}
              data-testid="input-enable-account"
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <span className="text-sm font-medium text-slate-700">Buatkan akun login sekarang</span>
          </label>
        )}
        {showAccountSection && (
          <>
            {kunciRole ? (
              <Field label="Role & Template Hak Akses">
                <p className="text-sm text-slate-700 py-2" data-testid="login-role-terkunci">
                  {(LOGIN_ROLES.find(r => r.v === roleAkun) || { l: roleAkun }).l}
                  {form.login_template_key && <span className="text-slate-500"> · template {form.login_template_key}</span>}
                  <span className="block text-xs text-slate-500">
                    {akunSendiri ? "Role & izin akun sendiri hanya bisa diubah superadmin."
                      : "Role & template izin hanya bisa diatur superadmin atau admin."}
                  </span>
                </p>
              </Field>
            ) : (
              <>
                <Field label="Role">
                  <Select value={roleAkun}
                    onChange={v => upd("login_role", v)}
                    tid="input-login-role"
                    options={LOGIN_ROLES} />
                </Field>
                <Field label="Template Hak Akses (opsional)">
                  <Select value={form.login_template_key || ""}
                    onChange={v => upd("login_template_key", v)}
                    tid="input-login-template"
                    options={[
                      { v: "", l: "— pakai default role —" },
                      ...permTemplates.map(t => ({ v: t.key, l: `${t.is_custom ? "★ " : ""}${t.label}` })),
                    ]} />
                </Field>
              </>
            )}
            {akunTerlindungi ? (
              <p className="text-xs text-slate-500 md:col-span-2" data-testid="login-password-terkunci">
                Password akun ber-role {roleAkun} hanya bisa direset {akunSuperadmin ? "superadmin" : "superadmin atau admin"}.
              </p>
            ) : (
              <Field label={isEdit ? "Reset Password (kosongkan bila tak berubah)" : `Password (default: ${suggestedPwd || "6 digit NIK"})`}>
                <Input type="text"
                  value={isEdit ? (form.login_password_reset || "") : (form.login_password || "")}
                  onChange={v => upd(isEdit ? "login_password_reset" : "login_password", v)}
                  tid="input-login-password" />
                {isEdit && (
                  // Sama dengan server (`employees.update_employee` →
                  // `authenticator.putuskan_karena_password_diatur`).
                  <span className="block text-[11px] text-slate-500" data-testid="login-password-reset-catatan">
                    Mengisi ini ikut memutus Google Authenticator akun tersebut (kalau tersambung), dan pemiliknya
                    wajib mengganti password saat login.
                  </span>
                )}
              </Field>
            )}
          </>
        )}
      </div>
    </div>
  );
}
